import type { Correction, CorrectionStudentWorkDetail } from '@kimuno/api-sdk';

import type { AsyncAction } from './common.actionTypes';

export enum CREATE_CORRECTION {
	START = 'CREATE_CORRECTION_START',
	SUCCESS = 'CREATE_CORRECTION_SUCCESS',
	ERROR = 'CREATE_CORRECTION_ERROR',
}

export enum GET_CORRECTION {
	START = 'GET_CORRECTION_START',
	SUCCESS = 'GET_CORRECTION_SUCCESS',
	ERROR = 'GET_CORRECTION_ERROR',
}

export enum UPDATE_CORRECTION {
	START = 'UPDATE_CORRECTION_START',
	SUCCESS = 'UPDATE_CORRECTION_SUCCESS',
	ERROR = 'UPDATE_CORRECTION_ERROR',
}

export enum UPDATE_CORRECTION_TITLE {
	START = 'UPDATE_CORRECTION_TITLE_START',
	SUCCESS = 'UPDATE_CORRECTION_TITLE_SUCCESS',
	ERROR = 'UPDATE_CORRECTION_TITLE_ERROR',
}

export enum DELETE_CORRECTION {
	START = 'DELETE_CORRECTION_START',
	SUCCESS = 'DELETE_CORRECTION_SUCCESS',
	ERROR = 'DELETE_CORRECTION_ERROR',
}

export enum DUPLICATE_CORRECTION {
	START = 'DUPLICATE_CORRECTION_START',
	SUCCESS = 'DUPLICATE_CORRECTION_SUCCESS',
	ERROR = 'DUPLICATE_CORRECTION_ERROR',
}

export enum ADD_STUDENT_WORK {
	START = 'ADD_STUDENT_WORK_START',
	SUCCESS = 'ADD_STUDENT_WORK_SUCCESS',
	ERROR = 'ADD_STUDENT_WORK_ERROR',
}

export enum REMOVE_STUDENT_WORK {
	START = 'REMOVE_STUDENT_WORK_START',
	SUCCESS = 'REMOVE_STUDENT_WORK_SUCCESS',
	ERROR = 'REMOVE_STUDENT_WORK_ERROR',
}

export enum RENAME_STUDENT_WORK {
	START = 'RENAME_STUDENT_WORK_START',
	SUCCESS = 'RENAME_STUDENT_WORK_SUCCESS',
	ERROR = 'RENAME_STUDENT_WORK_ERROR',
}

export enum GET_STUDENT_WORK {
	START = 'GET_STUDENT_WORK_START',
	SUCCESS = 'GET_STUDENT_WORK_SUCCESS',
	ERROR = 'GET_STUDENT_WORK_ERROR',
}

export enum UPDATE_STUDENT_WORK {
	START = 'UPDATE_STUDENT_WORK_START',
	SUCCESS = 'UPDATE_STUDENT_WORK_SUCCESS',
	ERROR = 'UPDATE_STUDENT_WORK_ERROR',
}

export enum RESOLVE_UNCERTAIN_SPAN {
	START = 'RESOLVE_UNCERTAIN_SPAN_START',
	SUCCESS = 'RESOLVE_UNCERTAIN_SPAN_SUCCESS',
	ERROR = 'RESOLVE_UNCERTAIN_SPAN_ERROR',
}

export enum EVALUATE_CORRECTION {
	START = 'EVALUATE_CORRECTION_START',
	SUCCESS = 'EVALUATE_CORRECTION_SUCCESS',
	ERROR = 'EVALUATE_CORRECTION_ERROR',
}

export type CreateCorrectionAction = AsyncAction<
	CREATE_CORRECTION.START,
	CREATE_CORRECTION.SUCCESS,
	CREATE_CORRECTION.ERROR,
	Correction
>;

export type GetCorrectionAction = AsyncAction<
	GET_CORRECTION.START,
	GET_CORRECTION.SUCCESS,
	GET_CORRECTION.ERROR,
	Correction,
	{ correctionId: string }
>;

export type UpdateCorrectionAction = AsyncAction<
	UPDATE_CORRECTION.START,
	UPDATE_CORRECTION.SUCCESS,
	UPDATE_CORRECTION.ERROR,
	Correction,
	{ correctionId: string }
>;

export type UpdateCorrectionTitleAction = AsyncAction<
	UPDATE_CORRECTION_TITLE.START,
	UPDATE_CORRECTION_TITLE.SUCCESS,
	UPDATE_CORRECTION_TITLE.ERROR,
	Correction,
	{ correctionId: string }
>;

export type DeleteCorrectionAction = AsyncAction<
	DELETE_CORRECTION.START,
	DELETE_CORRECTION.SUCCESS,
	DELETE_CORRECTION.ERROR,
	undefined,
	{ correctionId: string }
>;

// Keyed by the source; the result is the copy.
export type DuplicateCorrectionAction = AsyncAction<
	DUPLICATE_CORRECTION.START,
	DUPLICATE_CORRECTION.SUCCESS,
	DUPLICATE_CORRECTION.ERROR,
	Correction,
	{ correctionId: string }
>;

export type AddStudentWorkAction = AsyncAction<
	ADD_STUDENT_WORK.START,
	ADD_STUDENT_WORK.SUCCESS,
	ADD_STUDENT_WORK.ERROR,
	Correction,
	{ correctionId: string }
>;

export type RemoveStudentWorkAction = AsyncAction<
	REMOVE_STUDENT_WORK.START,
	REMOVE_STUDENT_WORK.SUCCESS,
	REMOVE_STUDENT_WORK.ERROR,
	Correction,
	{ correctionId: string; studentWorkId: string }
>;

export type RenameStudentWorkAction = AsyncAction<
	RENAME_STUDENT_WORK.START,
	RENAME_STUDENT_WORK.SUCCESS,
	RENAME_STUDENT_WORK.ERROR,
	Correction,
	{ correctionId: string; studentWorkId: string }
>;

export type GetStudentWorkAction = AsyncAction<
	GET_STUDENT_WORK.START,
	GET_STUDENT_WORK.SUCCESS,
	GET_STUDENT_WORK.ERROR,
	CorrectionStudentWorkDetail,
	{ correctionId: string; studentWorkId: string }
>;

export type UpdateStudentWorkAction = AsyncAction<
	UPDATE_STUDENT_WORK.START,
	UPDATE_STUDENT_WORK.SUCCESS,
	UPDATE_STUDENT_WORK.ERROR,
	CorrectionStudentWorkDetail,
	{ correctionId: string; studentWorkId: string }
>;

export type ResolveUncertainSpanAction = AsyncAction<
	RESOLVE_UNCERTAIN_SPAN.START,
	RESOLVE_UNCERTAIN_SPAN.SUCCESS,
	RESOLVE_UNCERTAIN_SPAN.ERROR,
	CorrectionStudentWorkDetail,
	{ correctionId: string; studentWorkId: string }
>;

export type EvaluateCorrectionAction = AsyncAction<
	EVALUATE_CORRECTION.START,
	EVALUATE_CORRECTION.SUCCESS,
	EVALUATE_CORRECTION.ERROR,
	Correction,
	{ correctionId: string }
>;

export type CorrectionsActionTypes =
	| CreateCorrectionAction
	| GetCorrectionAction
	| UpdateCorrectionAction
	| UpdateCorrectionTitleAction
	| DeleteCorrectionAction
	| DuplicateCorrectionAction
	| AddStudentWorkAction
	| RemoveStudentWorkAction
	| RenameStudentWorkAction
	| GetStudentWorkAction
	| UpdateStudentWorkAction
	| ResolveUncertainSpanAction
	| EvaluateCorrectionAction;
